import { default as enUS } from './en-US.json';

export type Languages = 'en-US' | 'ja-JP' | 'zh-CN' | 'zh-TW' | 'ko-KR';

export type Translations = {
  btnContinueWithPasskey?: string;
  btnCreatePasskeyWallet?: string;
  btnLoginWithPasskey?: string;
  usePasskeyToLoginSmartAccount?: string;
};

export type TransactionType = typeof enUS;

export type TransactionKey = keyof TransactionType;

export type Locales = {
  'en-US'?: Partial<TransactionType>;
  'ja-JP'?: Partial<TransactionType>;
  'zh-CN'?: Partial<TransactionType>;
  'zh-TW'?: Partial<TransactionType>;
  'ko-KR'?: Partial<TransactionType>;
};

const localeLoaders: Record<Languages, () => Promise<{ default: TransactionType }>> = {
  'en-US': () => Promise.resolve({ default: enUS }),
  'ja-JP': () => import('./ja-JP.json'),
  'zh-CN': () => import('./zh-CN.json'),
  'zh-TW': () => import('./zh-TW.json'),
  'ko-KR': () => import('./ko-KR.json'),
};

const localeCache = new Map<Languages, TransactionType>();
localeCache.set('en-US', enUS);

export const getLocale = (lang: Languages): TransactionType => {
  return localeCache.get(lang) ?? enUS;
};

export const loadLocale = async (lang: Languages): Promise<TransactionType> => {
  const cached = localeCache.get(lang);
  if (cached) return cached;

  const module = await localeLoaders[lang]();
  localeCache.set(lang, module.default);
  return module.default;
};

export function formatString(template: string, ...values: (string | number)[]): string {
  return template.replace(/\{\d+\}/g, (match) => {
    const index = parseInt(match.slice(1, -1), 10);
    return values[index]?.toString() ?? match;
  });
}

export function formatLocale(txt: string, ...values: (string | number | React.ReactNode)[]) {
  const nodePlacer = '<></>';
  const nodes: React.ReactNode[] = [];
  const formatValue = txt.replace(/\{\d+\}/g, (match) => {
    const index = parseInt(match.slice(1, -1), 10);
    const value = values[index];
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || !value) {
      return values[index]?.toString() ?? match; // Use the provided value or keep the placeholder if not provided
    }
    nodes.push(value);
    return nodePlacer;
  });
  if (nodes.length > 0) {
    const strs = formatValue.split(nodePlacer);
    const elements: React.ReactNode[] = [];
    for (let i = 0; i < strs.length; i++) {
      elements.push(strs[i]);
      if (nodes[i]) {
        elements.push(nodes[i]);
      }
    }
    return elements.map((item) => item);
  }
  return formatValue;
}
